import {
	DeviceUserPerspective,
	LocalizationSensitivity,
	ReflectionCorrection,
	VoiceActivityDetectionSensitivity
} from '@shure/shared/angular/data-access/system-api/core';
import {
	AnalogOutputMode,
	CoverageMode,
	DeviceAuthentication,
	DeviceModel,
	DeviceStatus,
	MeasurementUnit,
	NumberRange,
	Placement
} from '@shure/shared/angular/data-access/system-api/models';
import { PositionPoint, PositionUnit } from '@shure/shared/angular/utils/utils-position';

import { CoverageAudioChannel } from './coverage-audio-channel';
import { CoverageAnalogOutputChannel } from './coverage-audio-channel-passive-speaker';
import { CoverageAudioCoverageArea } from './coverage-audio-coverage-area';

export type FeatureValue<T> = { value: T; isMissing: boolean };
export type FeatureSet<T> = { [P in keyof T]?: FeatureValue<T[P]> };

export const DEVICE_DEFAULTS: Pick<CoverageDeviceFeatures, 'placement'> = {
	placement: Placement.WallHorizontal
};

/**
 * Room-relative rotations of the device.
 *
 * - X (heading): clockwise floor-plan facing (12→3 o'clock in top view)
 * - Y (tilt): ceiling ↔ wall face tilt
 * - Z (wall-turn): about LED / MXA horizontal flip when `zAxis === 180`.
 */
export type CoverageDeviceRoomRotation = {
	xAxis: number;
	yAxis?: number;
	zAxis: number;
};

/**
 * Device-local body rotations (sysapi orientation). +X right, +Y LED/top, +Z grille-out.
 *
 * - X (tilt): turn grille flush with ceiling to wall about +X
 * - Y (wall-turn): turn about +Y (LED); in wall installation, turn in the wall plane
 * - Z (roll): spin grille about +Z
 */
export type CoverageDeviceLocalRotation = {
	xAxis: number;
	yAxis: number;
	zAxis: number;
};

export type TalkerTrackingConfig = {
	localizationSensitivity?: LocalizationSensitivity;
	metricsIncluded?: boolean;
	positionUpdateRate?: number | null;
	reflectionCorrection?: ReflectionCorrection;
	voiceActivityDetectionSensitivity?: VoiceActivityDetectionSensitivity;
};

export type CoverageDeviceFeatures = {
	audioChannels: CoverageAudioChannel[];
	audioCoverageAreas: CoverageAudioCoverageArea[];
	analogOutputChannels: CoverageAnalogOutputChannel[];
	analogAudioOutputMode: AnalogOutputMode;
	authentication: DeviceAuthentication;
	coverageMode: CoverageMode;
	position: PositionPoint;
	rotation: CoverageDeviceRoomRotation;
	deviceLocalRotation: CoverageDeviceLocalRotation;
	placement: Placement;
	detectedPlacement?: Placement;
	height: PositionUnit;
	listenerHeight: PositionUnit;
	audioAutoFocus: { enabled: boolean };
	debugMode: { enabled: boolean };
	dspDebugMode: { enabled: boolean };
	identify: { identifying: boolean };
	talkerTrackingConfig?: TalkerTrackingConfig;
	name: string;
	coverageLock: { enabled: boolean };
	automixGainMetering: { enabled: boolean };
	mute: { muted: boolean };
	danteName: string;
	userPerspective: DeviceUserPerspective;
	measurementUnit: MeasurementUnit;
};

export type CoverageDevice = {
	id: string;
	model: DeviceModel;
	status: DeviceStatus;
	virtual: boolean;
	automixmodeManualChannelId?: string;
	features: FeatureSet<CoverageDeviceFeatures>;
	constraints?: {
		talkerTrackingConfig: {
			positionUpdateRate: NumberRange;
		};
		height?: {
			height: NumberRange;
		};
		name?: string;
	};
};

export type CoverageTalkerPosition = {
	confidenceLevel: number;
	x: PositionUnit;
	y: PositionUnit;
	z: PositionUnit;
};
